import React, { useEffect, useMemo, useRef, useState } from "react";
import { errorMessage } from "../../services";
import { getOverview } from "../../services/admin";
import { getSites } from "../../services/sites";
import { saveBanner } from "../../services/marketing";
import CouponsManager from "./CouponsManager";
import MarketingStrategyReport from "./MarketingStrategyReport";

// Marketing: make a banner ad, see it live, download it and share it. Everything is drawn in the browser on a canvas,
// so nothing is uploaded. Super Admin and partners promote Ownezzy (partners with their referral link); store owners
// promote their own store.
const SIZES = [
  { key: "square", label: "Instagram / Facebook post", note: "1080 × 1080", w: 1080, h: 1080 },
  { key: "story", label: "Instagram story / WhatsApp status", note: "1080 × 1920", w: 1080, h: 1920 },
  { key: "wide", label: "Facebook link / cover", note: "1200 × 630", w: 1200, h: 630 },
];

const THEMES = [
  { key: "forest", label: "Forest", from: "#123f2b", to: "#2f8b59", text: "#ffffff", accent: "#ffd166", button: "#ffd166", buttonText: "#173f2a" },
  { key: "sunset", label: "Sunset", from: "#ff7a45", to: "#ffb347", text: "#ffffff", accent: "#2b1b00", button: "#2b1b00", buttonText: "#ffd166" },
  { key: "royal", label: "Royal", from: "#1e2a78", to: "#6a3de8", text: "#ffffff", accent: "#7ef0c1", button: "#ffffff", buttonText: "#2b2f8f" },
  { key: "blush", label: "Blush", from: "#ffd6e5", to: "#fff1f6", text: "#4a1430", accent: "#d63c7a", button: "#d63c7a", buttonText: "#ffffff" },
  { key: "clean", label: "Clean", from: "#ffffff", to: "#eef6f0", text: "#14261b", accent: "#2f8b59", button: "#14261b", buttonText: "#ffffff" },
];

// "Custom": the person picks the colours; text on the button and badge is white or dark, whichever reads better
const readableOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#14261b" : "#ffffff";
};
const customTheme = (c) => ({ key: "custom", from: c.from, to: c.to, text: c.text, accent: c.button, button: c.button, buttonText: readableOn(c.button) });
const CUSTOM_FIELDS = [["from", "Background 1"], ["to", "Background 2"], ["text", "Text"], ["button", "Button"]];

const OWNEZZY_TEXT = {
  badge: "FREE TO START",
  headline: "Get your own online store website",
  subtext: "Sell on WhatsApp, Instagram and Facebook with your own website. Ready in minutes, no coding.",
  button: "Get it for free",
};
const storeText = (site) => ({
  badge: "SHOP ONLINE",
  headline: site ? `${site.name} is now online!` : "We are now online!",
  subtext: "Browse our products and order from home. Fast delivery and easy payment.",
  button: "Shop now",
});

// Splits text into lines that fit the width, at most maxLines (the last one gets "…" if cut)
function wrap(ctx, text, width, maxLines) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= width || !line) line = next;
    else { lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].replace(/\s+\S*$/, "")}…`;
    return kept;
  }
  return lines;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Draws the whole banner at full size. Measures first, then places: wide banners put the button and link on one
// row and the picture on the right; tall ones centre the words in the space above the button.
function draw(canvas, { size, theme, text, link, brand, image }) {
  const { w, h } = size;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const font = (weight, px) => `${weight} ${px}px 'DM Sans', Manrope, Arial, sans-serif`;
  const wide = w / h > 1.4;
  const tall = h / w > 1.4;
  const unit = wide ? (h / 630) * 0.85 : w / 1080; // sizes below are for a 1080px square
  const pad = 80 * unit;

  const bg = ctx.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, theme.from);
  bg.addColorStop(1, theme.to);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = theme.accent;
  ctx.beginPath(); ctx.arc(w * 0.92, h * 0.08, 260 * unit, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(w * 0.05, h * 0.95, 200 * unit, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;
  ctx.textBaseline = "top";

  const textWidth = wide && image ? w * 0.55 - pad : w - pad * 2;
  const headlinePx = (tall ? 124 : 88) * unit;
  const subPx = (tall ? 48 : 40) * unit;
  const maxLines = wide ? 2 : 4;
  ctx.font = font(800, headlinePx);
  const headlineLines = wrap(ctx, text.headline, textWidth, maxLines);
  ctx.font = font(500, subPx);
  const subLines = wrap(ctx, text.subtext, textWidth, maxLines);
  const badgeH = text.badge ? 88 * unit : 0;
  const blockH = badgeH + headlineLines.length * headlinePx * 1.12 + 18 * unit + subLines.length * subPx * 1.4;

  // Bottom row(s): the button, and the link under it (beside it on wide banners)
  const buttonH = (tall ? 112 : 96) * unit;
  const buttonPx = (tall ? 46 : 40) * unit;
  const linkPx = (tall ? 36 : 30) * unit;
  const bottom = h - pad;
  const linkBelow = link && !wide;
  const buttonY = bottom - buttonH - (linkBelow ? linkPx + 26 * unit : 0);
  const label = text.button ? `${text.button}  →` : "";
  ctx.font = font(800, buttonPx);
  const buttonW = label ? ctx.measureText(label).width + 96 * unit : 0;

  ctx.fillStyle = theme.text;
  ctx.font = font(800, 34 * unit);
  ctx.fillText(brand, pad, pad);
  const top = pad + 80 * unit;
  let y = tall && !image ? Math.max(top, top + (buttonY - top - blockH) / 2 - 40 * unit) : top;

  if (text.badge) {
    ctx.font = font(800, 28 * unit);
    const bw = ctx.measureText(text.badge).width + 44 * unit;
    ctx.fillStyle = theme.accent;
    roundRect(ctx, pad, y, bw, 54 * unit, 27 * unit);
    ctx.fill();
    ctx.fillStyle = theme.buttonText;
    ctx.fillText(text.badge, pad + 22 * unit, y + 13 * unit);
    y += badgeH;
  }
  ctx.font = font(800, headlinePx);
  ctx.fillStyle = theme.text;
  for (const line of headlineLines) {
    ctx.fillText(line, pad, y);
    y += headlinePx * 1.12;
  }
  y += 18 * unit;
  ctx.font = font(500, subPx);
  ctx.globalAlpha = 0.9;
  for (const line of subLines) {
    ctx.fillText(line, pad, y);
    y += subPx * 1.4;
  }
  ctx.globalAlpha = 1;

  if (image) {
    const box = wide
      ? { x: w * 0.58, y: pad, w: w * 0.42 - pad, h: h - pad * 2 }
      : { x: pad, y: y + 30 * unit, w: w - pad * 2, h: buttonY - y - 70 * unit };
    if (box.h > 120 * unit) {
      const scale = Math.max(box.w / image.width, box.h / image.height);
      const sw = box.w / scale;
      const sh = box.h / scale;
      ctx.save();
      roundRect(ctx, box.x, box.y, box.w, box.h, 36 * unit);
      ctx.clip();
      ctx.drawImage(image, (image.width - sw) / 2, (image.height - sh) / 2, sw, sh, box.x, box.y, box.w, box.h);
      ctx.restore();
    }
  }
  if (label) {
    ctx.fillStyle = theme.button;
    roundRect(ctx, pad, buttonY, buttonW, buttonH, buttonH / 2);
    ctx.fill();
    ctx.fillStyle = theme.buttonText;
    ctx.font = font(800, buttonPx);
    ctx.textBaseline = "middle";
    ctx.fillText(label, pad + 48 * unit, buttonY + buttonH / 2);
    ctx.textBaseline = "top";
  }
  if (link) {
    ctx.font = font(700, linkPx);
    ctx.fillStyle = theme.text;
    ctx.globalAlpha = 0.85;
    const shown = link.replace(/^https?:\/\//, "");
    if (linkBelow) ctx.fillText(shown, pad, bottom - linkPx);
    else {
      // Beside the button: shrink to the space left before the picture (or the edge)
      const x = pad + buttonW + (label ? 32 * unit : 0);
      const room = (image ? w * 0.55 : w - pad) - x;
      let px = linkPx;
      while (px > 14 * unit && ctx.measureText(shown).width > room) { px -= 1; ctx.font = font(700, px); }
      ctx.fillText(shown, x, buttonY + (buttonH - px) / 2);
    }
    ctx.globalAlpha = 1;
  }
}

export default function MarketingTab({ user, notify }) {
  const isOwner = user.role === "storeadmin";
  const isPartner = user.role === "subadmin";
  const [mainView, setMainView] = useState(user.role === "superadmin" ? "strategy" : "banner");
  const canvasRef = useRef(null);
  const [sizeKey, setSizeKey] = useState("square");
  const [themeKey, setThemeKey] = useState("forest");
  const [text, setText] = useState(isOwner ? storeText(null) : OWNEZZY_TEXT);
  const [link, setLink] = useState(isOwner ? "" : "https://ownezzy.com");
  const [links, setLinks] = useState([]); // [{ label, url }] to pick from
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(null);
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [custom, setCustom] = useState({ from: "#0f3d2e", to: "#2f8b59", text: "#ffffff", button: "#ffd166" });
  const [bannerLink, setBannerLink] = useState(null); // { key, url }: the saved copy of the banner as it looked then
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const size = SIZES.find((s) => s.key === sizeKey);
  const theme = themeKey === "custom" ? customTheme(custom) : THEMES.find((t) => t.key === themeKey);
  const brand = isOwner ? (site ? site.name : "Our store") : "Ownezzy";

  // The links to promote: the store's address, or ownezzy.com (partners: the demo stores with their referral code)
  useEffect(() => {
    if (isOwner) {
      getSites({ limit: 50 })
        .then(({ data }) => {
          setSites(data.sites);
          const first = data.sites[0];
          if (first) { setSite(first); setLink(first.url); setText(storeText(first)); }
        })
        .catch((err) => setError(errorMessage(err)));
    } else if (isPartner) {
      getOverview()
        .then(({ data }) => {
          const shareLinks = (data.stats.shareLinks || []).map((l) => ({ label: `${l.name} demo`, url: l.url }));
          setLinks(shareLinks);
          if (shareLinks[0]) setLink(shareLinks[0].url);
        })
        .catch((err) => setError(errorMessage(err)));
    }
  }, [isOwner, isPartner]);

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current, { size, theme, text, link, brand, image });
  }, [size, theme, text, link, brand, image]);

  const set = (key) => (e) => setText((t) => ({ ...t, [key]: e.target.value }));
  const pickSite = (slug) => {
    const next = sites.find((s) => s.id === slug);
    setSite(next);
    setLink(next ? next.url : "");
    setText(storeText(next));
  };
  // A picture from the web. The canvas can only be downloaded if that site allows its images to be used elsewhere.
  const applyImageUrl = () => {
    const url = imageUrl.trim();
    if (!/^https?:\/\//i.test(url)) return notify("Paste an image address starting with https://", "error");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { setImage(img); setImageUrl(""); };
    img.onerror = () => notify("This image could not be used. Save it to your phone or computer and use Add picture instead.", "error");
    img.src = url;
  };
  const pickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return notify("Choose an image file", "error");
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => notify("This image could not be opened", "error");
    img.src = url;
  };

  const fileName = `ownezzy-banner-${size.key}.png`;
  const toBlob = () => new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/png"));
  const download = async () => {
    const blob = await toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const caption = `${text.headline}${link ? `\n${link}` : ""}`;
  // "Copy banner link": saves the banner (JPEG, smaller to share) and copies its web address. The same banner is
  // saved only once; any change makes a new one.
  const bannerKey = JSON.stringify([sizeKey, theme, text, link, brand, image && image.src]);
  const copyBannerLink = async () => {
    setSaving(true);
    try {
      let url = bannerLink && bannerLink.key === bannerKey ? bannerLink.url : null;
      if (!url) {
        // Uploads stop at 3 MB (nginx), so a busy banner is saved at a lower quality
        let blob = null;
        for (const quality of [0.9, 0.75, 0.6]) {
          // eslint-disable-next-line no-await-in-loop
          blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/jpeg", quality));
          if (blob.size < 2.8 * 1024 * 1024) break;
        }
        const { data } = await saveBanner(new File([blob], `banner-${size.key}.jpg`, { type: "image/jpeg" }));
        url = new URL(data.url, window.location.origin).href;
        setBannerLink({ key: bannerKey, url });
      }
      try {
        await navigator.clipboard.writeText(url);
        notify("Banner link copied");
      } catch {
        notify("Banner saved. Copy the link below.");
      }
    } catch (err) {
      notify(err.name === "SecurityError" ? "This banner uses a picture from another website, so it can't be saved. Use Add picture instead." : errorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  };
  const currentBannerLink = bannerLink && bannerLink.key === bannerKey ? bannerLink.url : "";
  // Phones share the picture itself to Instagram, WhatsApp or Facebook; elsewhere it is downloaded first
  const shareImage = async () => {
    const blob = await toBlob();
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text: caption }); } catch (err) { if (err.name !== "AbortError") notify("Could not share. Download the banner instead.", "error"); }
      return;
    }
    await download();
    notify("Banner downloaded. Attach it to your post or status.");
  };
  const shareLinks = useMemo(() => link ? [
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(caption)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}` },
  ] : [], [link, caption]);

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">MARKETING &amp; SALES ENGINE</p>
          <h1>Marketing Command Center</h1>
          <p className="subheading">
            {user.role === "superadmin"
              ? "Master marketing strategy, regional video ad scripts, budget simulators, and creative tools to acquire Indian micro-sellers."
              : isOwner
              ? "Make a banner for your store, then post it on Instagram, WhatsApp or Facebook."
              : "Make a banner that sells Ownezzy websites, then post it on Instagram, WhatsApp or Facebook."}
          </p>
        </div>
      </div>

      {/* Main Mode Switcher Tabs */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "20px",
          paddingBottom: "4px",
          overflowX: "auto",
        }}
      >
        <button
          type="button"
          onClick={() => setMainView("strategy")}
          style={{
            background: mainView === "strategy" ? "#0f172a" : "#f1f5f9",
            color: mainView === "strategy" ? "#ffffff" : "#475569",
            border: "none",
            padding: "9px 18px",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "13.5px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          <span>📊 Growth Strategy &amp; Playbook</span>
          {user.role === "superadmin" && (
            <span
              style={{
                fontSize: "10px",
                background: "#10b981",
                color: "#ffffff",
                padding: "2px 7px",
                borderRadius: "10px",
                fontWeight: 800,
              }}
            >
              Master Plan
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setMainView("banner")}
          style={{
            background: mainView === "banner" ? "#0f172a" : "#f1f5f9",
            color: mainView === "banner" ? "#ffffff" : "#475569",
            border: "none",
            padding: "9px 18px",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "13.5px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          <span>🎨 Banner Ads Maker</span>
        </button>

        {(isOwner || user.role === "superadmin") && (
          <button
            type="button"
            onClick={() => setMainView("coupons")}
            style={{
              background: mainView === "coupons" ? "#0f172a" : "#f1f5f9",
              color: mainView === "coupons" ? "#ffffff" : "#475569",
              border: "none",
              padding: "9px 18px",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "13.5px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <span>🏷️ Discount Coupons</span>
          </button>
        )}
      </div>

      {mainView === "strategy" && <MarketingStrategyReport notify={notify} />}

      {mainView === "banner" && (
        <>
          {error && <div className="admin-error">{error}</div>}

          <div className="marketing-layout">
            <section className="panel marketing-form">
              <div className="admin-field">Size</div>
              <div className="marketing-sizes">
                {SIZES.map((s) => (
                  <button key={s.key} type="button" className={sizeKey === s.key ? "selected" : ""} onClick={() => setSizeKey(s.key)}>
                    <i className={`marketing-size-${s.key}`} aria-hidden="true" /><b>{s.label}</b><small>{s.note}</small>
                  </button>
                ))}
              </div>

              <div className="admin-field">Colours</div>
              <div className="marketing-themes">
                {THEMES.map((t) => (
                  <button key={t.key} type="button" className={themeKey === t.key ? "selected" : ""} onClick={() => setThemeKey(t.key)} style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})` }} aria-label={t.label} title={t.label} />
                ))}
                <button type="button" className={`marketing-custom-swatch ${themeKey === "custom" ? "selected" : ""}`} onClick={() => setThemeKey("custom")} aria-label="Custom colours" title="Custom colours" />
              </div>
              {themeKey === "custom" && (
                <div className="marketing-custom-colors">
                  {CUSTOM_FIELDS.map(([key, label]) => (
                    <label key={key}><input type="color" value={custom[key]} onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))} /><span>{label}</span></label>
                  ))}
                </div>
              )}

              {isOwner && sites.length > 1 && (
                <label className="admin-field">Store<select value={site ? site.id : ""} onChange={(e) => pickSite(e.target.value)}>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
              )}
              <label className="admin-field">Badge<input value={text.badge} maxLength={24} onChange={set("badge")} /></label>
              <label className="admin-field">Headline<input value={text.headline} maxLength={70} onChange={set("headline")} /></label>
              <label className="admin-field">Text<textarea rows={3} value={text.subtext} maxLength={160} onChange={set("subtext")} /></label>
              <div className="form-row">
                <label className="admin-field">Button<input value={text.button} maxLength={24} onChange={set("button")} /></label>
                <label className="admin-field">Link
                  {links.length > 0
                    ? <select value={link} onChange={(e) => setLink(e.target.value)}>{links.map((l) => <option key={l.url} value={l.url}>{l.label}</option>)}<option value="https://ownezzy.com">ownezzy.com</option></select>
                    : <input value={link} maxLength={120} onChange={(e) => setLink(e.target.value)} />}
                </label>
              </div>
              {isPartner && <p className="field-help">Your links carry your referral code, so shops that sign up from them become your customers.</p>}
              <div className="admin-field">Picture <span className="muted-note">(optional, e.g. your products)</span></div>
              <div className="marketing-image-row">
                <label className="outline-btn">{image ? "Change picture" : "Add picture"}<input type="file" accept="image/*" hidden onChange={pickImage} /></label>
                {image && <button type="button" className="text-btn" onClick={() => setImage(null)}>Remove</button>}
              </div>
              <div className="marketing-url-row">
                <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyImageUrl(); } }} placeholder="or paste an image address (https://…)" aria-label="Image address" />
                <button type="button" className="outline-btn" onClick={applyImageUrl} disabled={!imageUrl.trim()}>Use</button>
              </div>
            </section>

            <section className="panel marketing-preview">
              <div className="panel-heading"><div><h2>Preview</h2><p>{size.label} · {size.note}</p></div></div>
              <div className={`marketing-canvas-wrap marketing-canvas-${size.key}`}>
                <canvas ref={canvasRef} aria-label="Banner preview" />
              </div>
              <div className="marketing-actions">
                <button className="primary-btn" onClick={download}>⬇ Download PNG</button>
                <button className="outline-btn" onClick={shareImage}>Share banner</button>
                <button className="outline-btn" onClick={copyBannerLink} disabled={saving}>{saving ? "Saving…" : "🔗 Copy banner link"}</button>
                {shareLinks.map((s) => <a key={s.label} className="outline-btn" href={s.href} target="_blank" rel="noopener noreferrer">{s.label}</a>)}
              </div>
              {currentBannerLink && (
                <div className="marketing-banner-link">
                  <span>Banner link</span>
                  <input readOnly value={currentBannerLink} onFocus={(e) => e.target.select()} aria-label="Banner link" />
                  <a className="text-btn" href={currentBannerLink} target="_blank" rel="noopener noreferrer">Open</a>
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {mainView === "coupons" && (isOwner || user.role === "superadmin") && (
        <CouponsManager user={user} notify={notify} />
      )}
    </>
  );
}
